import { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSubmitFeedbackMutation } from '@/store/api/apiSlice';
import { FiMessageSquare, FiX, FiStar, FiSend, FiCamera, FiUpload, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas-pro';
import { getApiError } from '@/utils/errorUtils';

// Order matters: longer/more-specific prefixes must come first.
const MODULE_MAP: Record<string, string> = {
  '/admin/org-settings': 'org-settings',
  '/ai-studio': 'ai-studio',
  '/ai-whiteboard': 'ai-whiteboard',
  '/dashboard': 'dashboard',
  '/board': 'board',
  '/backlog': 'backlog',
  '/sprints': 'sprints',
  '/handbook': 'handbook',
  '/milestones': 'milestones',
  '/list': 'tasks',
  '/calendar': 'calendar',
  '/gantt': 'gantt',
  '/workload': 'workload',
  '/time-tracking': 'time-tracking',
  '/reports': 'reports',
  '/stakeholder': 'stakeholder',
  '/members': 'members',
  '/teams': 'teams',
  '/notifications': 'notifications',
  '/projects': 'projects',
  '/settings': 'settings',
  '/billing': 'billing',
  '/integrations': 'integrations',
  '/webhooks': 'webhooks',
  '/workflows': 'workflows',
  '/roles': 'roles',
  '/admin': 'admin',
};

function detectModule(pathname: string): string {
  for (const [prefix, mod] of Object.entries(MODULE_MAP)) {
    if (pathname.startsWith(prefix)) return mod;
  }
  return 'general';
}

interface FeedbackWidgetProps {
  placement?: 'floating' | 'header';
}

export default function FeedbackWidget({ placement = 'floating' }: FeedbackWidgetProps) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();

  const module = detectModule(location.pathname);

  const captureScreenshot = async () => {
    setCapturing(true);
    setOpen(false);
    
    try {
      // Wait a moment for modal to close
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const canvas = await html2canvas(document.body, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        scale: 1,
      });
      
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
          setScreenshot(file);
          setScreenshotPreview(URL.createObjectURL(blob));
        }
        setCapturing(false);
        setOpen(true);
      });
    } catch (error) {
      console.error('Screenshot failed:', error);
      toast.error('Failed to capture screenshot');
      setCapturing(false);
      setOpen(true);
    }
  };

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('module', module);
      formData.append('rating', rating.toString());
      if (comment.trim()) formData.append('comment', comment.trim());
      if (screenshot) formData.append('screenshot', screenshot);

      await submitFeedback(formData).unwrap();
      toast.success('Thanks for your feedback!');
      setOpen(false);
      setRating(0);
      setComment('');
      removeScreenshot();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to submit feedback'));
    }
  };

  return (
    <div className={placement === 'header' ? 'relative' : ''}>
      <button
        onClick={() => setOpen(!open)}
        className={
          placement === 'header'
            ? 'w-9 h-9 flex items-center justify-center rounded-[var(--radius-sm)] text-text-secondary hover:bg-surface-hover transition-colors'
            : 'fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-brand text-white shadow-lg hover:bg-brand-dark transition-all flex items-center justify-center'
        }
        title="Send Feedback"
        aria-label="Send Feedback"
      >
        {open ? <FiX size={placement === 'header' ? 16 : 20} /> : <FiMessageSquare size={placement === 'header' ? 16 : 20} />}
      </button>

      {/* Popup panel */}
      {open && (
        <div
          className={
            placement === 'header'
              ? 'absolute right-0 mt-2 z-50 w-80 bg-surface rounded-xl shadow-2xl border border-border overflow-hidden'
              : 'fixed bottom-20 right-6 z-50 w-80 bg-surface rounded-xl shadow-2xl border border-border overflow-hidden animate-in slide-in-from-bottom-2'
          }
        >
          <div className="p-4 border-b border-border bg-surface-hover">
            <h3 className="font-semibold text-text-primary text-sm">Send Feedback</h3>
            <p className="text-xs text-text-secondary mt-0.5">
              Module: <span className="font-medium capitalize">{module}</span>
            </p>
          </div>

          <div className="p-4 space-y-4">
            {/* Star rating */}
            <div>
              <label className="text-xs font-medium text-text-secondary mb-2 block">
                How would you rate this?
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHovered(star)}
                    onMouseLeave={() => setHovered(0)}
                    className="p-0.5 transition-transform hover:scale-110"
                  >
                    <FiStar
                      size={24}
                      className={
                        star <= (hovered || rating)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-gray-300'
                      }
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Comment */}
            <div>
              <label className="text-xs font-medium text-text-secondary mb-1.5 block">
                Comments (optional)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 2000))}
                placeholder="Tell us what you think..."
                rows={3}
                className="w-full px-3 py-2 bg-bg border border-border rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand/30 resize-none"
              />
              <p className="text-[10px] text-text-muted text-right">{comment.length}/2000</p>
            </div>

            {/* Screenshot/Attachment */}
            <div>
              <label className="text-xs font-medium text-text-secondary mb-1.5 block">
                Attachment (optional)
              </label>
              
              {screenshotPreview ? (
                <div className="relative group">
                  <img 
                    src={screenshotPreview} 
                    alt="Screenshot" 
                    className="w-full h-32 object-cover rounded-lg border border-border"
                  />
                  <button
                    onClick={removeScreenshot}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <FiTrash2 size={14} />
                  </button>
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 text-white text-[10px] rounded">
                    {screenshot?.name}
                  </div>
                </div>
              ) : (
                <div 
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    isDragging ? 'border-brand bg-brand/5' : 'border-border'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={captureScreenshot}
                        disabled={capturing}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-brand/10 text-brand rounded-md text-xs hover:bg-brand/20 disabled:opacity-50"
                      >
                        <FiCamera size={14} />
                        {capturing ? 'Capturing...' : 'Capture'}
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-bg border border-border rounded-md text-xs hover:bg-surface-hover"
                      >
                        <FiUpload size={14} />
                        Upload
                      </button>
                    </div>
                    <p className="text-[10px] text-text-muted">or drag & drop an image</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    className="hidden"
                  />
                </div>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={isLoading || rating === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiSend size={14} />
              {isLoading ? 'Sending...' : 'Submit Feedback'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
